import { useSiteConfig } from "@/contexts/SiteConfigContext";

// Every piece of fixed text on the public site. The admin's "Page copy" screen is built
// from this list, and anything saved there (site_config.page_copy[key]) replaces the default.
// [key, label, default, multiline]
export const COPY_GROUPS = [
  {
    group: "Navigation",
    items: [
      ["nav.projects", "Menu: projects", "Projects"],
      ["nav.about", "Menu: about", "About"],
      ["nav.blog", "Menu: blog", "Blog"],
      ["nav.survey", "Menu: survey", "Survey"],
      ["nav.contact", "Menu: contact button", "Contact"],
    ],
  },
  {
    group: "Home",
    items: [
      ["home.hero_title", "Hero headline", "Backends that"],
      ["home.hero_accent", "Hero headline, highlighted end", "stay reliable."],
      ["home.hero_sub", "Hero text (blank uses the hero sub-headline from Site Config)", "", true],
      ["home.speed_label", "Speed control label", "Animation speed"],
      ["home.gauge_years", "Gauge 1 label (years)", "Experience"],
      ["home.gauge_projects", "Gauge 2 label (projects)", "Projects"],
      ["home.gauge_skills", "Gauge 3 label (skills)", "Skills"],
      ["home.gauge_posts", "Gauge 4 label (posts)", "Blog posts"],
      ["home.about_kicker", "About block kicker", "About"],
      ["home.about_title", "About block headline", "About"],
      ["home.about_accent", "About block headline, highlighted part", "me"],
      ["home.about_title_end", "About block headline, end", ""],
      ["home.about_extra", "About block second paragraph", "I like taking systems apart, finding what fails first, and making them faster without making them fragile.", true],
      ["home.about_cta", "About block button", "More about me"],
      ["home.projects_kicker", "Projects kicker", "Projects"],
      ["home.projects_title", "Projects headline", "Featured"],
      ["home.projects_accent", "Projects headline, highlighted end", "projects"],
      ["home.projects_sub", "Projects intro", "Open a project for the full case study: the problem, the trade-offs, and what I'd change.", true],
      ["home.skills_kicker", "Skills kicker", "Skills"],
      ["home.skills_title", "Skills headline", "What I work with"],
      ["home.blog_kicker", "Blog kicker", "Blog"],
      ["home.blog_title", "Blog headline", "Latest posts"],
      ["home.blog_cta", "Blog button", "All posts"],
    ],
  },
  {
    group: "Contact",
    items: [
      ["contact.kicker", "Kicker", "Contact"],
      ["contact.title", "Headline", "Get in"],
      ["contact.accent", "Headline, highlighted end", "touch."],
      ["contact.sub", "Text", "Hiring, have a project in mind, or just want to talk? Send me a message.", true],
      ["contact.reply_time", "Reply-time line on the contact page", "I reply within two working days."],
      ["contact.form_title", "Form title", "Send a message"],
      ["contact.kinds", "Topic chips (comma-separated)", "Hiring, A project, Just chatting"],
      ["contact.submit", "Send button", "Send message"],
      ["contact.sent_title", "Sent: headline", "Message sent."],
      ["contact.sent_body", "Sent: text ({name} is replaced)", "Thanks, {name}. I'll reply within two working days."],
    ],
  },
  {
    group: "Projects pages",
    items: [
      ["projects.kicker", "Kicker", "Projects"],
      ["projects.title", "Headline", "Projects I've"],
      ["projects.accent", "Headline, highlighted end", "built."],
      ["projects.sub", "Intro", "Production systems, side projects and experiments. Each project covers the problem, the trade-offs, the numbers, and what I'd change.", true],
      ["projects.empty", "Empty filter message", "No projects in this category yet."],
      ["project.back", "Case study: back link", "← All projects"],
    ],
  },
  {
    group: "About page",
    items: [
      ["about.kicker", "Kicker", "About"],
      ["about.card_label", "Profile card label", "Profile"],
      ["about.experience_kicker", "Experience kicker", "Experience"],
      ["about.experience_title", "Experience headline", "Work history"],
      ["about.experience_sub", "Experience intro", "Most recent first."],
      ["about.principles_kicker", "Principles kicker", "Principles"],
      ["about.principles_title", "Principles headline", "How I work"],
      ["about.skills_kicker", "Skills kicker", "Skills"],
      ["about.skills_title", "Skills headline", "What I work with"],
      ["about.hobbies_kicker", "Hobbies kicker", "Outside work"],
      ["about.hobbies_title", "Hobbies headline", "Hobbies"],
      ["about.hobbies_sub", "Hobbies intro", "What I work on in my spare time."],
      ["about.resume", "Résumé button", "Download résumé (PDF)"],
    ],
  },
  {
    group: "Blog",
    items: [
      ["blog.kicker", "Kicker", "Blog"],
      ["blog.title", "Headline", "Latest"],
      ["blog.accent", "Headline, highlighted end", "posts."],
      ["blog.sub", "Intro", "Notes on distributed systems, Postgres and running production software. Written after something broke, and I learned why.", true],
      ["blog.empty_title", "Empty: headline", "No posts yet."],
      ["blog.empty_body", "Empty: text", "The first post is on its way."],
      ["post.back", "Post: back link", "← All posts"],
      ["post.byline", "Post: author line (after your role)", ""],
    ],
  },
  {
    group: "Survey",
    items: [
      ["survey.kicker", "Kicker", "Survey"],
      ["survey.done_kicker", "Finish: kicker", "Complete"],
      ["survey.done_title", "Finish: headline", "Thanks for taking the survey."],
      ["survey.closed_title", "No survey running: headline", "No survey"],
      ["survey.closed_accent", "No survey running: highlighted end", "right now."],
      ["survey.closed_body", "No survey running: text", "There's no survey running right now. When I'm deciding what to build next, this is where I'll ask for your input.", true],
    ],
  },
  {
    group: "404 page",
    items: [
      ["404.kicker", "Kicker", "Error 404"],
      ["404.title", "Headline", "Page not"],
      ["404.accent", "Headline, highlighted end", "found."],
      ["404.body", "Text", "This page doesn't exist. The link may be old, or the page may have moved."],
      ["404.cta", "Button", "Back to home"],
    ],
  },
  {
    group: "Footer",
    items: [["footer.line", "Footer line ({name} and {year} are replaced)", "© {year} {name}"]],
  },
];

const DEFAULTS = Object.fromEntries(COPY_GROUPS.flatMap((g) => g.items.map(([k, , d]) => [k, d])));

export function useCopy() {
  const { config } = useSiteConfig();
  const saved = config?.page_copy || {};
  return (key, vars) => {
    let text = typeof saved[key] === "string" && saved[key].trim() ? saved[key] : DEFAULTS[key] ?? "";
    if (vars) Object.entries(vars).forEach(([k, v]) => { text = text.split(`{${k}}`).join(v ?? ""); });
    return text;
  };
}
